import { cn } from '../../lib/utils';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'outlineHighlight'
  | 'ghost'
  | 'destructive'
  | 'link';

export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

/**
 * UI-kit columns — maps to DS tokens in `ds-authenticated.css` (`:root` fallbacks in classes).
 * Use with {@link ButtonAppearance} for filled vs outline matrix from the design kit.
 */
export type ButtonColorTone =
  | 'primary'
  | 'orange'
  | 'green'
  | 'blue'
  | 'amber'
  | 'red'
  | 'neutral';

/** Filled (solid) vs outline — kit rows 1–2 vs 5–6; disabled rows use `disabled` prop */
export type ButtonAppearance = 'filled' | 'outline';

const baseStyles =
  'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0';

/** Spinner inherits `currentColor` — works on filled + outline */
const loadingSpinnerCompatible =
  '[&_.animate-spin]:border-current data-[loading=true]:text-current';

/** Outline stroke: 1px solid Gray-600 (see `globals.css` `--Color-Gray-600`) */
const outlineBorderGray =
  'border border-solid border-[var(--Color-Gray-600,#757575)]';

/** Neutral hover — avoids `--theme-accent` (e.g. private `#fce4f1`) which reads pink on outlines */
const outlineHoverNeutral =
  'hover:bg-foreground/5 active:bg-foreground/10';

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-primary-600 text-primary-foreground hover:bg-primary-700 active:bg-primary-800 focus-visible:outline-primary-600 disabled:opacity-50 [&_svg]:size-4',
  secondary:
    'border border-border bg-card text-foreground hover:bg-accent active:bg-accent/80 focus-visible:outline-primary-600 disabled:opacity-50 [&_svg]:size-4',
  outline: cn(
    outlineBorderGray,
    'bg-transparent text-foreground',
    outlineHoverNeutral,
    'focus-visible:outline-primary-600 disabled:opacity-50 [&_svg]:size-4',
  ),
  outlineHighlight:
    'border border-solid border-highlight bg-transparent text-highlight hover:bg-highlight/15 active:bg-highlight/25 focus-visible:outline-highlight disabled:opacity-50 [&_svg]:size-4',
  ghost:
    'text-foreground hover:bg-accent hover:text-accent-foreground active:bg-accent/80 focus-visible:outline-primary-600 disabled:opacity-50 [&_svg]:size-4',
  destructive:
    'bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/80 focus-visible:outline-destructive disabled:opacity-50 [&_svg]:size-4',
  link: 'h-auto min-h-0 rounded-md bg-transparent px-0 py-1 text-primary-600 underline-offset-4 hover:underline active:text-primary-700 focus-visible:outline-primary-600 disabled:opacity-50 [&_svg]:size-4',
};

/** Large / small from kit — md stays middle for legacy */
const sizeStyles: Record<ButtonSize, string> = {
  sm: 'h-8 rounded-lg px-3 text-xs [&_svg]:size-4',
  md: 'h-10 rounded-xl px-4 text-sm [&_svg]:size-4',
  lg: 'h-11 rounded-xl px-5 text-base [&_svg]:size-4',
  icon: 'size-10 rounded-xl p-0 [&_svg]:size-4',
};

/** Shared disabled surfaces for kit (solid + outline rows 3–4 / 7–8 in spec) */
const kitFilledDisabled =
  'disabled:opacity-100 disabled:bg-[var(--color-actions-disabled-bg-light-mode,rgba(0,0,0,0.06))] disabled:text-[var(--color-text-elements-disabled-text-light-mode,#9b90ad)] disabled:hover:bg-[var(--color-actions-disabled-bg-light-mode,rgba(0,0,0,0.06))] disabled:active:bg-[var(--color-actions-disabled-bg-light-mode,rgba(0,0,0,0.06))]';

const kitOutlineDisabled =
  'disabled:opacity-100 disabled:border-[var(--color-gray-300-light-mode,#e0e0e0)] disabled:bg-transparent disabled:text-[var(--color-text-elements-disabled-text-light-mode,#9b90ad)] disabled:hover:bg-transparent';

function kitFilledClasses(tone: ButtonColorTone): string {
  const common = cn(
    'border-transparent shadow-none',
    kitFilledDisabled,
    loadingSpinnerCompatible,
  );
  switch (tone) {
    case 'primary':
      return cn(
        common,
        'bg-[var(--color-primary-main-p-light-mode,#ed2991)] text-[var(--color-primary-contrast-p-light-mode,#ffffff)] hover:bg-[var(--color-primary-dark-p-light-mode,#8e1156)] active:bg-[#751247] focus-visible:outline-[#ed2991]',
      );
    case 'orange':
      return cn(
        common,
        'bg-[var(--color-secondary-main-s-light-mode,#f47521)] text-[var(--color-secondary-contrast-s-light-mode,#ffffff)] hover:bg-[var(--color-secondary-dark-s-light-mode,#8a3e0b)] active:bg-[#6d3309] focus-visible:outline-[#f47521]',
      );
    case 'green':
      return cn(
        common,
        'bg-[var(--color-feedback-success-main-su-light-mode,#17a83d)] text-[var(--color-feedback-success-contrast-su-light-mode,#ffffff)] hover:bg-[var(--color-feedback-success-dark-su-light-mode,#006e25)] active:bg-[#005a1f] focus-visible:outline-[#17a83d]',
      );
    case 'blue':
      return cn(
        common,
        'bg-[var(--color-feedback-info-main-i-light-mode,#2366bd)] text-[var(--color-feedback-info-contrast-i-light-mode,#ffffff)] hover:bg-[var(--color-feedback-info-dark-i-light-mode,#1a4d8f)] active:bg-[#163e6b] focus-visible:outline-[#2366bd]',
      );
    case 'amber':
      return cn(
        common,
        'bg-[var(--color-feedback-warning-main-w-light-mode,#e87f12)] text-[var(--color-feedback-warning-contrast-w-light-mode,#ffffff)] hover:bg-[var(--color-feedback-warning-dark-w-light-mode,#a75c0d)] active:bg-[#8a4b14] focus-visible:outline-[#e87f12]',
      );
    case 'red':
      return cn(
        common,
        'bg-[var(--color-feedback-error-main-e-light-mode,#dd3730)] text-[var(--color-feedback-error-contrast-e-light-mode,#ffffff)] hover:bg-[var(--color-feedback-error-dark-e-light-mode,#680003)] active:bg-[#5c0003] focus-visible:outline-[#dd3730]',
      );
    case 'neutral':
      return cn(
        common,
        'bg-[var(--color-gray-500-light-mode,#9e9e9e)] text-[var(--color-common-white-light-mode,#ffffff)] hover:bg-[var(--color-gray-700-light-mode,#616161)] active:bg-[var(--color-gray-800-light-mode,#424242)] focus-visible:outline-[#9e9e9e]',
      );
  }
}

function kitOutlineClasses(tone: ButtonColorTone): string {
  /** Border matches label color — do not reuse {@link outlineBorderGray} here. */
  const common = cn(
    'border border-solid bg-transparent shadow-none',
    outlineHoverNeutral,
    kitOutlineDisabled,
    loadingSpinnerCompatible,
  );
  switch (tone) {
    case 'primary':
      return cn(
        common,
        'border-[var(--color-primary-main-p-light-mode,#ed2991)] text-[var(--color-primary-main-p-light-mode,#ed2991)] focus-visible:outline-[#ed2991]',
      );
    case 'orange':
      return cn(
        common,
        'border-[var(--color-secondary-main-s-light-mode,#f47521)] text-[var(--color-secondary-main-s-light-mode,#f47521)] focus-visible:outline-[#f47521]',
      );
    case 'green':
      return cn(
        common,
        'border-[var(--color-feedback-success-main-su-light-mode,#17a83d)] text-[var(--color-feedback-success-main-su-light-mode,#17a83d)] focus-visible:outline-[#17a83d]',
      );
    case 'blue':
      return cn(
        common,
        'border-[var(--color-feedback-info-main-i-light-mode,#2366bd)] text-[var(--color-feedback-info-main-i-light-mode,#2366bd)] focus-visible:outline-[#2366bd]',
      );
    case 'amber':
      return cn(
        common,
        'border-[var(--color-feedback-warning-main-w-light-mode,#e87f12)] text-[var(--color-feedback-warning-main-w-light-mode,#e87f12)] focus-visible:outline-[#e87f12]',
      );
    case 'red':
      return cn(
        common,
        'border-[var(--color-feedback-error-main-e-light-mode,#dd3730)] text-[var(--color-feedback-error-main-e-light-mode,#dd3730)] focus-visible:outline-[#dd3730]',
      );
    case 'neutral':
      return cn(
        common,
        'border-[var(--color-gray-700-light-mode,#616161)] text-[var(--color-gray-700-light-mode,#616161)] focus-visible:outline-[#9e9e9e]',
      );
  }
}

export type ButtonStyleProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
  /** When set, uses design-kit matrix (7 tones × filled/outline × disabled) instead of legacy `variant` colors */
  colorTone?: ButtonColorTone;
  /** Default `filled`. Only applies with `colorTone` */
  appearance?: ButtonAppearance;
};

export function buttonVariants({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className,
  colorTone,
  appearance = 'filled',
}: ButtonStyleProps = {}) {
  const isLink = variant === 'link' && !colorTone;

  const kitActive = Boolean(colorTone);
  const kitClasses = kitActive
    ? appearance === 'outline'
      ? kitOutlineClasses(colorTone!)
      : kitFilledClasses(colorTone!)
    : null;

  const legacyVariantClasses = kitActive
    ? ''
    : variantStyles[variant];

  return cn(
    baseStyles,
    kitClasses,
    !isLink && sizeStyles[size],
    !kitActive && legacyVariantClasses,
    fullWidth && 'w-full',
    className,
  );
}
